<template>
    <div class="rule-set">
        <div class="tile-content tile-content-secondary" v-if="!isBinds">
            <label-filter @select="applyFilters"/>
        </div>
        <table class="table table-compact" v-if="hasItems && hasFilterResults">
            <colgroup>
                <col style="width:44px;">
                <col style="width:100px;">
                <col style="width:100px;">
                <col>
                <col style="width: 150px;">
            </colgroup>
            <thead>
            <tr>
                <th></th>
                <th>
                    <v-icon-button
                        :disabled="hasActiveFilters || cantUnlock"
                        type="basic"
                        size="small"
                        :tooltip="unlockLabel"
                        @click="toggleLock">
                        <v-icon :icon="unlockIcon"/>
                    </v-icon-button>
                    <span>Order</span>
                </th>
                <th>Status</th>
                <th>
                    <div class="grid-fitted">
                        <div class="grid-item">
                            {{show}}
                        </div>
                        <div class="grid-item no-flex">
                            <span class="text-muted text-normal-weight">{{filterResultsLabel}}</span>
                        </div>
                    </div>
                </th>
                <th class="text-right">
                    <router-link
                        :to="historyPageRoute"
                        class="inline-s text-muted"
                        style="font-weight:normal;text-transform: none">View event history
                    </router-link>
                </th>
            </tr>
            </thead>
            <draggable
                :list="filteredItems"
                v-bind="draggableOptions"
                @end="commitSort"
                :disabled="lockedReordering",
                tag="tbody">
                <tr
                    v-for="(rule, ruleIndex) in filteredItems"
                    :key="ruleIndex">
                    <td>
                        <div class="drag-handle">
                            <v-icon-button
                                type="basic"
                                size="small"
                                :disabled="cantSort">
                                <v-icon icon="drag"/>
                            </v-icon-button>
                        </div>
                    </td>
                    <td>
                        <div class="grid-content grid-fitted">
                            <div class="grid-item grid-item-full">
                                <v-icon-button
                                    type="basic"
                                    size="small"
                                    @click="orderRuleUp(rule.order)"
                                    :disabled="isFirstItem(rule.order)">
                                    <v-icon icon="caret-up"/>
                                </v-icon-button>
                            </div>
                            <div class="grid-item grid-item-full">
                                <v-icon-button
                                    type="basic"
                                    size="small"
                                    @click="orderRuleDown(rule.order)"
                                    :disabled="isLastItem(rule.order)">
                                    <v-icon icon="caret-down"/>
                                </v-icon-button>
                            </div>
                        </div>
                    </td>
                    <td>
                        <v-toggle
                            :value="true"
                            @input="toggleStatus"
                            v-model="rule.status"/>
                    </td>
                    <td style="cursor:pointer" @click="toggleRuleDetails(rule)">
                        <div v-if="isDetailsExpanded(rule)" class="stack-m"></div>
                        <div class="grid-fitted align-items-center" :class="{'stack-m': isDetailsExpanded(rule)}">
                            <div class="grid-item">
                                <span class="inline-xxs">{{rule.name}}</span>
                                <v-icon :icon="ruleCaretIcon(rule)" class="inline-s"/>
                                <span v-if="!isBinds && hasLabels(rule)">
                                    <r-badge class="inline-xs" v-for="(label,index) in rule.labels" :key="index">
                                        {{label | capitalize}}
                                    </r-badge>
                                </span>
                            </div>
                            <div class="grid-item no-flex" v-if="!isBinds && rule.final">
                                <v-icon icon="stop" class="inline-s"/>
                                <span class="text-muted">Stops further rule processing</span>
                            </div>
                        </div>
                        <rule-summary
                            v-if="isDetailsExpanded(rule)"
                            :rule="rule"
                            :event="event"/>
                    </td>
                    <td class="text-right">
                        <v-icon-button
                            type="basic"
                            tooltip="Clone"
                            @click="cloneRule(rule)"
                            class="inline-s">
                            <v-icon icon="clone"/>
                        </v-icon-button>
                        <v-icon-button
                            type="basic"
                            tooltip="Edit"
                            @click="editRule(rule)"
                            class="inline-s">
                            <v-icon icon="edit"/>
                        </v-icon-button>
                        <v-icon-button
                            type="basic"
                            tooltip="Delete"
                            @click="deleteRule(rule)">
                            <v-icon icon="trash"/>
                        </v-icon-button>
                    </td>
                </tr>
            </draggable>
        </table>
        <v-no-content v-else-if="hasItems && !hasFilterResults">
            <span slot="title">No {{show}} match your filters.</span>
            <p slot="description">
                Remove filters to search again.
            </p>
        </v-no-content>
        <v-no-content v-else>
            <span slot="title">It seems that you have no {{show}}.</span>
            <p slot="description">
                Create a new {{singularType}} to get started
            </p>
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages[addRoute.name].isAllowed"
                    :to="addRoute"
                    class="btn btn-link">Add {{show}}<v-icon class="margin-left-xs" icon="arrow-right"/></router-link>
            </div>
        </v-no-content>
    </div>
</template>

<script>
    import EventModel from '@/common/models/rules/event-model';
    import RuleFactory from '@/common/models/rules/rule-factory';
    import {
        vIcon, vIconButton, vToggle,
    } from '@/components/form';
    import vNoContent from '@/components/v-no-content';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import RuleModel from '@/common/models/rules/rule-model';
    import RuleSummary from '@/modules/rules-engine/actions/rule-summary';
    import Base58 from '@/common/helpers/base58';
    import _ from '@/common/helpers';
    import draggable from 'vuedraggable';
    import draggableDefaults from '@/vendor/draggable/defaults';
    import LabelFilter from './label-filter';

    export default {
        name: 'rule-set',
        components: {
            vIcon,
            vIconButton,
            vToggle,
            vNoContent,
            LabelFilter,
            RuleSummary,
            draggable,
        },
        props: {
            show: {
                type: String,
                default: RuleModel.Types.rule.family,
                validator: type => Object.values(RuleModel.Types).map(item => item.family).includes(type),
            },
            ruleSet: Object,
            event: {
                type: EventModel,
            },
        },
        computed: {
            historyPageRoute() {
                return {
                    name: 'view-system-event-history',
                    params: {
                        eventType: this.$route.params.eventType,
                        version: this.version,
                    },
                };
            },
            isBinds() {
                return this.show === RuleModel.Types.bind.family;
            },
            hasItems() {
                return this.items.length > 0;
            },
            unlockLabel() {
                const label = this.lockedReordering
                    ? 'Unlock Order'
                    : 'Lock Order';
                if (this.cantUnlock) {
                    return 'Not enough items to sort';
                }
                return this.hasActiveFilters
                    ? 'Order locked while filtering'
                    : label;
            },
            unlockIcon() {
                return this.lockedReordering
                    ? 'lock'
                    : 'unlock';
            },
            hasActiveFilters() {
                return this.activeFilters.length > 0;
            },
            filteredItems() {
                if (this.isBinds || !this.hasActiveFilters) {
                    return this.items;
                }
                return this.items
                    .filter(item => item.labels.length && item.labels
                        .some(lbl => this.activeFilters.includes(lbl)));
            },
            hasFilterResults() {
                return this.filteredItems.length > 0;
            },
            cantUnlock() {
                return this.filteredItems.length < 2;
            },
            cantSort() {
                return this.filteredItems.length < 2
                    || this.lockedReordering
                    || this.hasActiveFilters;
            },
            filterResultsLabel() {
                if (!this.isBinds && this.hasActiveFilters) {
                    return `(Showing ${this.filteredItems.length} ${this.singularType} of ${this.items.length})`;
                }
                return '';
            },
            singularType() {
                return this.show.substr(0, this.show.length - 1);
            },
            version() {
                return this.ruleSet.version;
            },
            addRoute() {
                return {name: `add-system-${this.isBinds ? 'bind' : 'rule'}`};
            },
        },
        watch: {
            ruleSet(set) {
                this.items = [...set[this.show]];
            },
        },
        data() {
            return {
                lockedReordering: true,
                items: [...this.ruleSet[this.show]],
                activeFilters: [],
                expandedRules: [],
                draggableOptions: {
                    ...draggableDefaults,
                    // force fallback when lots of elements present due to chrome bug
                    // https://github.com/SortableJS/Sortable/issues/1418
                    forceFallback: true,
                },
            };
        },
        methods: {
            isFirstItem(index) {
                return this.cantSort || index === 0;
            },
            isLastItem(index) {
                return this.cantSort || index === this.items.length - 1;
            },
            toggleLock() {
                this.lockedReordering = !this.lockedReordering;
            },
            lockReorder() {
                this.lockedReordering = true;
            },
            applyFilters(filters) {
                this.lockReorder();
                this.activeFilters = filters;
            },
            reorderRule(fromIndex, toIndex) {
                this.items.splice(toIndex, 0, ...this.items.splice(fromIndex, 1));
                this.triggerUpdate(`${_.capitalize(this.singularType)} order has been modified successfully.`);
            },
            keepToggleState(index, target) {
                if (this.expandedRules.includes(index)) {
                    this.toggleRuleDetails({order: index});
                    if (!this.expandedRules.includes(target)) {
                        this.toggleRuleDetails({order: target});
                    }
                }
            },
            orderRuleUp(originalIndex) {
                this.reorderRule(originalIndex, originalIndex - 1);
                // keep rule open if it was before being moved
                this.keepToggleState(originalIndex, originalIndex - 1);
            },
            orderRuleDown(originalIndex) {
                this.reorderRule(originalIndex, originalIndex + 1);
                // keep rule open if it was before being moved
                this.keepToggleState(originalIndex, originalIndex + 1);
            },
            hasLabels(rule) {
                return rule.labels.length > 0;
            },
            toggleStatus() {
                this.triggerUpdate(`${_.capitalize(this.singularType)} status has been updated successfully.`);
            },
            triggerUpdate(message = null) {
                const oppositeList = this.isBinds ? RuleModel.Types.rule.family : RuleModel.Types.bind.family;
                this.$emit('input', {
                    ruleSet: {
                        ...this.ruleSet,
                        [this.show]: this.items.map(item => item.getPayload()),
                        [oppositeList]: this.ruleSet[oppositeList].map(item => item.getPayload()),
                    },
                    message,
                });
            },
            ruleCaretIcon(rule) {
                return this.isDetailsExpanded(rule)
                    ? 'caret-down'
                    : 'caret-right';
            },
            isDetailsExpanded(rule) {
                return this.expandedRules.includes(rule.order);
            },
            toggleRuleDetails({order}) {
                if (this.expandedRules.includes(order)) {
                    this.expandedRules.splice(this.expandedRules.indexOf(order), 1);
                } else {
                    this.expandedRules.push(order);
                }
            },
            async cloneRule(rule) {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: `Clone ${this.singularType} "${rule.name}"`,
                    content: `Clone "${rule.name}" into a new ${this.singularType}? It will be turned off by default.`,
                    confirmLabel: 'Clone',
                };
                if (await confirmDialog(dialogParams)) {
                    const clonedRule = RuleFactory.create(this.show, rule.getPayload());
                    clonedRule.name = `Copy of ${rule.name}`;
                    clonedRule.status = false;
                    this.items.splice(rule.order + 1, 0, clonedRule);
                    this.triggerUpdate(`${_.capitalize(this.singularType)} has been cloned successfully.`);
                }
            },
            editRule(rule) {
                const itemIndex = Base58.encode(rule.order);
                const name = `edit-system-${this.isBinds ? 'bind' : 'rule'}`;
                this.$router.push({
                    name,
                    params: {
                        itemIndex,
                        eventType: this.$route.params.eventType,
                    },
                });
            },
            async deleteRule(rule) {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: `Delete ${this.singularType} "${rule.name}"`,
                    content: `Are you sure you want to delete this ${this.singularType}?`,
                    confirmLabel: 'Delete',
                    dangerConfirm: true,
                };
                if (await confirmDialog(dialogParams)) {
                    this.items.splice(rule.order, 1);
                    this.triggerUpdate(`${_.capitalize(this.singularType)} has been deleted successfully.`);
                }
            },
            /**
             * Triggered when the drag and drop component
             * ends the sorting.
             */
            commitSort({newIndex, oldIndex}) {
                this.keepToggleState(oldIndex, newIndex);
                this.triggerUpdate(`${_.capitalize(this.singularType)} order has been modified successfully.`);
            },
        },
    };
</script>
